import { useEffect, useState } from 'react';
import { EmailRecordsV1 } from '@frontend/api-email-records';
import { GlobalBannerDynamic } from '@frontend/global-info-banner';
import { Trans, useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { Text, TextLink, useModalControl } from '@frontend/design-system';
import { LearnMoreModal } from './learn-more-modal';

export const DomainVerifyBanner = () => {
  const { t } = useTranslation('bulk-messaging');
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const { selectedOrgId: orgId } = useAppScopeStore();
  const [showBanner, setShowBanner] = useState(false);

  const { modalProps: learnMoreModalProps, openModal: openLearnMoreModal } = useModalControl();

  const { data: orgDomainRecord } = EmailRecordsV1.Queries.useGetDomainsByOrgIDQuery({
    orgId,
  });

  // The domain verify feature is controlled through a feature flag instead of an ACL
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const domainVerifyFlag = getFeatureFlagValue('domain-verify-settings');

  useEffect(() => {
    if (domainVerifyFlag === false || (orgDomainRecord?.domains && orgDomainRecord?.domains?.length > 0)) {
      setShowBanner(false);
    } else if (isDomainVerifyBannerActive()) {
      setShowBanner(true);
    }
  }, [orgDomainRecord?.domains?.[0]?.id, domainVerifyFlag]);

  return showBanner ? (
    <>
      <GlobalBannerDynamic
        id='domain-verify-banner'
        surfaceId='banner.domain-verify'
        type='warning'
        title={t('Complete Your Email Marketing Setup')}
        message={
          <Text>
            <Trans t={t}>
              Please link your business email to unlock your full message allowance.{' '}
              <TextLink onClick={openLearnMoreModal}>Learn more...</TextLink>
            </Trans>
          </Text>
        }
        action={{ label: t('Link Business Email'), onClick: () => settingsNavigate({ to: '/email' }) }}
        isDismissible
        onDismiss={() => {
          setShowBanner(false);
          dismissDomainVerifyBanner();
        }}
      />
      <LearnMoreModal modalProps={learnMoreModalProps} />
    </>
  ) : null;
};

const HIDE_DOMAIN_VERIFY_BANNER_UNTIL = 'hide-domain-verify-banner-until';
const ONE_WEEK = 7 * 24 * 60 * 60 * 1000;

const dismissDomainVerifyBanner = () => {
  const hideUntilDate = new Date();
  hideUntilDate.setTime(hideUntilDate.getTime() + ONE_WEEK);
  localStorage.setItem(HIDE_DOMAIN_VERIFY_BANNER_UNTIL, hideUntilDate.toISOString());
};

const isDomainVerifyBannerActive = () => {
  const hideUntilDate = localStorage.getItem(HIDE_DOMAIN_VERIFY_BANNER_UNTIL);
  return !hideUntilDate || new Date(hideUntilDate) < new Date();
};
